// Правка объекта. До 06.09.2026 карточку объекта можно было только ЗАКРЫТЬ: маршрут правки на
// сервере есть с первого дня, а звать его было некому.
//
// Цена этого выше, чем кажется. Город и регион в форме заведения тоже не спрашивались, а без
// них объект не попадает ни в отбор людей по региону, ни в отчёты по городам — и починить это
// было нечем, кроме как завести объект заново. У заказчика объектов шестьдесят четыре, от
// Перми до Иркутской области.
//
// Часовой пояс правится здесь же и стоит с прямым предупреждением: он определяет, к какой дате
// относится ночная смена, а значит — в какой месяц попадут часы и деньги.
//
// Заказчик и юрлицо НЕ правятся: объект принадлежит заказчику по договору, а юрлицо задаёт
// нумерацию ведомостей. Перевод объекта к другому заказчику — это не правка поля, а разговор
// с бумагами; закрытие объекта делается своей кнопкой в карточке, со своим подтверждением.

import type { FormEvent } from 'react';
import { useState } from 'react';

import { RiBuilding4Line, RiErrorWarningLine } from '@remixicon/react';

import type { ApiError } from '@/shared/errors/api-error';
import type { SitePatch } from '@/shared/api/endpoints/org';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { Skeleton } from '@/shared/ui/screen-state';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextField } from '@/shared/ui/text-field';
import {
  useContractsQuery,
  usePatchSiteMutation,
  useSiteQuery,
  useSiteTypesQuery,
} from '@/shared/org/queries';

import type { ТочкаКарты } from '../components/КартаОбъекта';
import { ЗонаНаКарте, местоСтрокой, РАДИУС_ПО_УМОЛЧАНИЮ } from '../components/ЗонаНаКарте';
import { ВыборСтаршего } from '../components/ВыборСтаршего';
import { SITE_TIMEZONES } from '../timezones';

/** Правка объекта — то же право, что и заведение (server/app/api/v1/dictionaries.py). */
const EDIT_PERMISSION = 'core.dictionary.edit';

function илиПусто(значение: string): string | null {
  const очищенное = значение.trim();
  return очищенное === '' ? null : очищенное;
}

export function EditSitePanel({ id, onClose }: { id: string; onClose: () => void }) {
  const права = useAuthStore((state) => state.permissions);
  const можно = can(права, EDIT_PERMISSION);
  const карточка = useSiteQuery(id);
  const правка = usePatchSiteMutation(id);
  const типы = useSiteTypesQuery();

  const [черновик, setЧерновик] = useState<{
    название: string;
    город: string;
    регион: string;
    адрес: string;
    место: string;
    радиус: string;
    обход: string;
    старший: string;
    пояс: string;
    тип: string;
    договор: string;
  } | null>(null);
  // Область держится отдельным состоянием, а не строкой черновика: это список точек, и
  // складывать его в строку ради единообразия значило бы разбирать её обратно при каждом кадре.
  // `undefined` означает «ещё не трогали» и отличается от `null` — «убрали обведённое».
  const [обл, setОбл] = useState<ТочкаКарты[] | null | undefined>(undefined);
  const [ошибка, setОшибка] = useState<string | undefined>(undefined);
  const [заслон, setЗаслон] = useState<string | null>(null);

  const договоры = useContractsQuery(
    { customer_id: карточка.data?.customer_id },
    карточка.data !== undefined,
  );

  if (карточка.data === undefined) {
    return (
      <div className='flex flex-col gap-3'>
        <Skeleton className='h-10 w-full' />
        <Skeleton className='h-10 w-full' />
        <Skeleton className='h-10 w-2/3' />
      </div>
    );
  }

  const объект = карточка.data;
  // `undefined` — область не трогали, берём записанную; `null` — обведённое убрали.
  const область = обл === undefined ? (объект.geofence_polygon ?? null) : обл;
  const поля = черновик ?? {
    название: объект.name,
    город: объект.city ?? '',
    регион: объект.region ?? '',
    адрес: объект.address ?? '',
    // Уже записанная точка показывается в том же виде, в каком её примут обратно. Не показать
    // её значит стереть при первом же сохранении соседнего поля — и стереть молча.
    место: местоСтрокой(объект),
    радиус: String(объект.geofence_radius_m || РАДИУС_ПО_УМОЛЧАНИЮ),
    обход: объект.patrol_interval_hours === null ? '' : String(объект.patrol_interval_hours),
    старший: объект.field_manager_employee_id ?? '',
    пояс: объект.timezone,
    тип: объект.site_type_id ?? NO_VALUE,
    договор: объект.contract_id ?? NO_VALUE,
  };

  function поставить(ключ: keyof typeof поля, значение: string) {
    setЧерновик({ ...поля, [ключ]: значение });
  }

  function сохранить(событие: FormEvent<HTMLFormElement>) {
    событие.preventDefault();
    if (!можно) {
      setЗаслон(
        'Править объекты может тот, у кого есть право на справочники. Попросите администратора выдать его — набранное никуда не денется.',
      );
      return;
    }
    if (правка.isPending) {
      setЗаслон('Объект уже сохраняется, ждём ответ сервера. Второй запрос не отправляем.');
      return;
    }
    if (поля.название.trim() === '') {
      setОшибка('Название стереть нельзя: по нему объект находят в списке и в ведомости.');
      return;
    }

    setОшибка(undefined);
    setЗаслон(null);
    const тело: SitePatch = {
      name: поля.название.trim(),
      city: илиПусто(поля.город),
      region: илиПусто(поля.регион),
      address: илиПусто(поля.адрес),
      // Пустая строка здесь означает «убрать точку», а не «не трогать»: объект переехал, и
      // старая точка после переезда врёт хуже, чем её отсутствие.
      map_point: поля.место.trim(),
      geofence_radius_m: Number(поля.радиус),
      geofence_polygon: область,
      patrol_interval_hours: поля.обход === '' ? null : Number(поля.обход),
      field_manager_employee_id: поля.старший === '' ? null : поля.старший,
      timezone: поля.пояс,
      site_type_id: поля.тип === NO_VALUE ? null : поля.тип,
      contract_id: поля.договор === NO_VALUE ? null : поля.договор,
    };
    правка.mutate(тело, { onSuccess: onClose });
  }

  return (
    <form onSubmit={сохранить} noValidate className='flex flex-col gap-3'>
      {!можно && (
        <Alert.Root variant='light' status='warning' size='small' role='status'>
          <Alert.Icon as={RiErrorWarningLine} />
          <span>
            Права на правку справочников у вас нет: форму можно заполнить, но сохранить объект не
            выйдет. Право выдаёт администратор.
          </span>
        </Alert.Root>
      )}

      <TextField
        id='edit-site-name'
        label='Название'
        required
        icon={RiBuilding4Line}
        value={поля.название}
        onChange={(значение) => поставить('название', значение)}
        error={ошибка}
      />

      <div className='grid grid-cols-2 gap-2'>
        <TextField
          id='edit-site-city'
          label='Город'
          value={поля.город}
          onChange={(значение) => поставить('город', значение)}
          placeholder='Сургут'
          hint='По городу собираются отчёты и подбирается резерв.'
        />
        <TextField
          id='edit-site-region'
          label='Регион'
          value={поля.регион}
          onChange={(значение) => поставить('регион', значение)}
          placeholder='ХМАО'
        />
      </div>

      <TextField
        id='edit-site-address'
        label='Адрес'
        value={поля.адрес}
        onChange={(значение) => поставить('адрес', значение)}
        hint='Куда ехать заступающему.'
      />

      <ВыборСтаршего
        id='edit-site-manager'
        значение={поля.старший === '' ? null : поля.старший}
        наВыбор={(значение) => поставить('старший', значение ?? '')}
      />

      <ЗонаНаКарте
        префикс='edit-site'
        место={поля.место}
        радиус={Number(поля.радиус)}
        область={область}
        обход={поля.обход === '' ? null : Number(поля.обход)}
        наМесто={(значение) => поставить('место', значение)}
        наРадиус={(значение) => поставить('радиус', String(значение))}
        наОбласть={setОбл}
        наОбход={(значение) => поставить('обход', значение === null ? '' : String(значение))}
      />

      <SelectField
        id='edit-site-timezone'
        label='Часовой пояс объекта'
        required
        value={поля.пояс}
        onChange={(значение) => поставить('пояс', значение)}
        options={SITE_TIMEZONES.map((зона) => ({ value: зона.id, label: зона.title }))}
        placeholder='Выберите пояс'
        hint='Смена относится к дате заступления по поясу объекта: правка поля меняет, в какой месяц попадут ночные часы.'
      />

      <SelectField
        id='edit-site-type'
        label='Тип объекта'
        value={поля.тип}
        onChange={(значение) => поставить('тип', значение)}
        options={(типы.data?.items ?? []).map((строка) => ({
          value: строка.id,
          label: строка.title,
        }))}
        noneLabel='Тип не задан'
        placeholder='Выберите тип объекта'
        pending={типы.isPending}
        failed={типы.isError}
        failedError={типы.error as ApiError | null}
        emptyNote='Типов объектов пока нет — объект останется без типа, а шаблон ведомости придётся назначить ему отдельно.'
        hint='От типа объект наследует шаблон ведомости, если своего у него нет.'
      />

      <SelectField
        id='edit-site-contract'
        label='Договор'
        value={поля.договор}
        onChange={(значение) => поставить('договор', значение)}
        options={(договоры.data?.items ?? []).map((строка) => ({
          value: строка.id,
          label: строка.number === null ? 'Договор без номера' : `№ ${строка.number}`,
        }))}
        noneLabel='Без договора'
        placeholder='Выберите договор'
        pending={договоры.isPending}
        failed={договоры.isError}
        failedError={договоры.error as ApiError | null}
        emptyNote='У заказчика нет заведённых договоров.'
      />

      {правка.isError && <ErrorAlert error={правка.error} />}

      {заслон !== null && (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          {заслон}
        </Hint.Root>
      )}

      <div className='flex items-center gap-2 pt-1'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {правка.isPending ? 'Сохраняем…' : 'Сохранить'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>
    </form>
  );
}
